// Storage and navigation are supplied by the client. The v1 app can use
// the same retry rules and API contract without depending on Vuex.
export function installAuthInterceptors(api, session) {
	let refreshPromise = null
	const isAuthRequest = config => /(?:^|\/)(login|register|refresh|logout|password\/(email|reset))$/.test((config.url || '').split('?')[0])

	api.interceptors.request.use(config => {
		const token = config.skipAuth ? '' : session.getToken()
		config.headers = config.headers || {}
		delete config.headers.Authorization
		if (token) config.headers.Authorization = `Bearer ${token}`
		config.authToken = token
		config.authSessionId = session.getSessionId()
		return config
	})

	const refresh = (failedToken, sessionId) => {
		if (!refreshPromise) {
			refreshPromise = session.withRefreshLock(async () => {
				if (session.getSessionId() !== sessionId) throw new Error('Session changed')
				const current = session.getToken()
				if (!current) throw new Error('Session ended')
				if (current !== failedToken) return current
				try {
					const { data } = await api.post('refresh', null, { skipAuthRefresh: true })
					if (session.getSessionId() !== sessionId) throw new Error('Session changed')
					// A login/logout while the request was in flight wins.
					if (session.getToken() !== current) {
						if (!session.getToken()) throw new Error('Session ended')
						return session.getToken()
					}
					if (!data || typeof data.token !== 'string' || !data.token) throw new Error('Invalid refresh response')
					session.saveToken(data.token)
					return data.token
				} catch (error) {
					if (session.getSessionId() !== sessionId) throw error
					if (error.response && error.response.status === 401) {
						const latest = session.getToken()
						if (latest && latest !== current) return latest
						session.expire(current)
					}
					throw error
				}
			}).finally(() => { refreshPromise = null })
		}
		return refreshPromise
	}

	api.interceptors.response.use(response => response, async error => {
		const { config, response } = error
		if (!config || !response || response.status !== 401 || config.skipAuthRefresh || isAuthRequest(config)) throw error
		if (config.authSessionId !== session.getSessionId()) throw error
		if (config.authRetried || !session.getToken()) {
			session.expire(config.authToken)
			throw error
		}
		config.authRetried = true
		await refresh(config.authToken, config.authSessionId)
		if (config.authSessionId !== session.getSessionId()) throw error
		return api(config)
	})
}
